import { FC, Fragment, useEffect, useRef, useState } from "react";
import { Box, Flex, useDisclosure } from "@smile2impress/components/relaunch";
import { isClient } from "@smile2impress/attraction-utils";

import { useAutoCloseMenu } from "../../hooks/useAutoCloseMenu";
import { useCloseMenuEscapeButton } from "../../hooks/useCloseMenuEscapeButton";
import { ButtonsGroup } from "./components/ButtonsGroup";
import { MobileMenuProps } from "./MobileMenu.types";
import { MenuItem } from "./components/MenuItem";
import { mobileMenuTestIdsConfig } from "../../configs/headerTestIds";

export const MobileMenu: FC<MobileMenuProps> = ({
    cta,
    ctaExpanded,
    contacts,
    menuTitle,
    items,
    expandableMobileMenu = true,
    expandableMessengerButton = false,
}) => {
    const { isOpen, onToggle, onClose } = useDisclosure();
    const [windowHeight, setWindowHeight] = useState(
        isClient() ? window.innerHeight : 0
    );
    const [isExpanded, setExpanded] = useState(false);
    const buttonsGroupRef = useRef<HTMLDivElement>(null);

    const scrollWatcher = () => {
        // User should scroll a bit before we show hime expanded menu
        if (expandableMobileMenu)
            setExpanded(window.scrollY > windowHeight * 0.07);
    };

    const resizeWatcher = () => {
        setWindowHeight(window.innerHeight);
    };

    const closeMenu = () => {
        onClose();
    };

    useAutoCloseMenu(onClose);
    useCloseMenuEscapeButton({ isOpen, closeMenu: onClose });

    useEffect(() => {
        window.addEventListener("scroll", scrollWatcher);
        window.addEventListener("resize", resizeWatcher);
        window.addEventListener("beforeunload", closeMenu);
        setWindowHeight(window.innerHeight);
        return () => {
            window.removeEventListener("scroll", scrollWatcher);
            window.removeEventListener("resize", resizeWatcher);
            window.removeEventListener("beforeunload", closeMenu);
        };
    }, []);

    const transition = "all .3s ease-in";

    return (
        <Flex
            position="fixed"
            bottom={"24px"}
            right={2}
            left={2}
            zIndex="modal"
            h={4}
            direction="column"
        >
            <Box
                bgColor="base.blue"
                position="fixed"
                borderRadius={"sm"}
                top={isOpen ? 2 : "100500px"}
                left={2}
                right={2}
                p={1}
                bottom={
                    isOpen
                        ? windowHeight -
                          (buttonsGroupRef?.current?.getBoundingClientRect()
                              .top || 0) +
                          2
                        : "-100500px"
                }
                overflowY="auto"
                transition={transition}
                boxShadow={"medium"}
            >
                {items.map((menuItem, index) => (
                    <Fragment key={index}>
                        <MenuItem {...menuItem} />
                    </Fragment>
                ))}
            </Box>
            <Box
                ref={buttonsGroupRef}
                zIndex="docked"
                mr={isExpanded && !isOpen ? "auto" : "0"}
                ml="auto"
                display="inline-block"
                transition={transition}
                width={isOpen ? "100%" : "auto"}
            >
                <ButtonsGroup
                    cta={cta}
                    ctaExpanded={ctaExpanded}
                    contacts={contacts}
                    menuTitle={menuTitle}
                    onToggle={onToggle}
                    isOpen={isOpen}
                    isExpanded={isExpanded}
                    testIds={mobileMenuTestIdsConfig}
                    expandableMessengerButton={expandableMessengerButton}
                />
            </Box>
        </Flex>
    );
};
